import React, { useContext } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import Paginator from '../../components/Paginator';
import { getUserLanguage } from '../../utils/LanguageUtil';
import { sortUrl, toSortOrder, parse } from '../../utils/UrlUtil';
import AssessmentListTableHeader from './AssessmentListTableHeader';
import { AccountContext } from '../../components/AccountContext';
import { useSelector } from 'react-redux';

const messages = defineMessages({
  new: { id: 'candidate.status.NEW', defaultMessage: 'NEW' },
  invited: { id: 'candidate.status.INVITED', defaultMessage: 'INVITED' },
  started: { id: 'candidate.status.STARTED', defaultMessage: 'IN PROGRESS' },
  declined: { id: 'candidate.status.DECLINED', defaultMessage: 'DECLINED' },
  expired: { id: 'candidate.status.EXPIRED', defaultMessage: 'EXPIRED' },
  submitted: { id: 'candidate.status.SUBMITTED', defaultMessage: 'ASSESSED' },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  template: { id: 'attrs.assessmentTemplate', defaultMessage: 'Assessment Template' },
  language: { id: 'attrs.language', defaultMessage: 'Language' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
});

const getItemStatusProperties = (status, formatMessage) => {
  switch (status) {
    case 'NEW': {
      return {
        styleClass: 'filter is-active',
        text: formatMessage(messages.new),
      };
    }
    case 'INVITED': {
      return {
        styleClass: 'filter filter--info is-active',
        text: formatMessage(messages.invited),
      };
    }
    case 'STARTED': {
      return {
        styleClass: 'filter filter--warning is-active',
        text: formatMessage(messages.started),
      };
    }
    case 'DECLINED': {
      return {
        styleClass: 'filter filter--error is-active',
        text: formatMessage(messages.declined),
      };
    }
    case 'EXPIRED': {
      return {
        styleClass: 'filter filter--error is-active',
        text: formatMessage(messages.expired),
      };
    }
    case 'SUBMITTED': {
      return {
        styleClass: 'filter filter--success is-active',
        text: formatMessage(messages.submitted),
      };
    }
    default: {
      return {};
    }
  }
};

const statusTemplate = (rowData, { formatMessage }) => {
  const statusProp = getItemStatusProperties(rowData.status, formatMessage);
  const { styleClass, text } = statusProp;
  if (statusProp) {
    return <span className={styleClass}>{text}</span>;
  }

  return null;
};

const nameTemplate = (rowData, { currentAccount }) => {
  const detailUrl = `/accounts/${currentAccount}/assessments/${rowData.id}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData.title}
    </Link>
  );
};

const languageTemplate = (rowData, col) => {
  const language = getUserLanguage(rowData.locale, col.translatedTags);
  return language.label;
};

const createdTemplate = (rowData, { formatDate }) => {
  const created = formatDate(moment(rowData.createdOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return created;
};

const AssessmentList = ({ connection: { edges, totalCount, indexFrom, indexTo, pageInfo }, loading }) => {
  const location = useLocation();
  const history = useHistory();
  const currentAccount = useContext(AccountContext);
  const { formatMessage, formatDate } = useIntl();
  const { translatedTags } = useSelector((state) => state.i18n);

  const onSortChange = (event) => {
    sortUrl(event, location, history);
  };

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  // data list
  const listRows = edges.map(({ node }) => ({
    ...node,
    title: node.respondent ? `${node.respondent.firstName} ${node.respondent.lastName}` : '',
    template: node.template ? node.template.title : '',
  }));

  // header
  const nameHeader = formatMessage(messages.name);
  const templateHeader = formatMessage(messages.template);
  const languageHeader = formatMessage(messages.language);
  const statusHeader = formatMessage(messages.status);
  const createdHeader = formatMessage(messages.created);

  // footer
  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-col-12">
                <AssessmentListTableHeader />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={listRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={onSortChange}
                  lazy
                  loading={loading}
                  autoLayout
                >
                  <Column
                    field="title"
                    header={nameHeader}
                    currentAccount={currentAccount}
                    body={nameTemplate}
                    sortable
                    style={{ textAlign: 'left' }}
                  />
                  <Column field="template" header={templateHeader} sortable />
                  <Column
                    field="locale"
                    header={languageHeader}
                    body={languageTemplate}
                    translatedTags={translatedTags}
                    sortable
                  />
                  <Column
                    field="status"
                    header={statusHeader}
                    body={statusTemplate}
                    formatMessage={formatMessage}
                    sortable
                  />
                  <Column
                    field="createdOn"
                    header={createdHeader}
                    formatDate={formatDate}
                    body={createdTemplate}
                    sortable
                  />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

AssessmentList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default AssessmentList;
